import { useCallback, useState } from "react";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { useMMMonitoring } from "../../hooks/api/admin/useMMMonitoring";
import { SPACING } from "../../constants/design/spacing";
import useScreenSize from "../../hooks/api/screenSize/useScreenSize";
import { MarketDropdown } from "../Dropdown/MarketDropdown";

const ENV_OPTIONS = [
  { value: "testnet", label: "testnet" },
  { value: "mainnet", label: "mainnet" },
];

const MM_PROCESS_OPTIONS = [
  { value: "perp_mm", label: "perp_mm" },
  { value: "perp_hedger", label: "perp_hedger" },
  { value: "options_hedger", label: "options_hedger" },
  { value: "options_mm", label: "options_mm" },
];

// Singleton services have no per-asset instances (e.g. account_monitor runs once on perp1).
const SINGLETON_PROCESSES = ["account_monitor"];

// The single-process controls also expose singletons; "Manage All" stays per-asset only.
const SINGLE_PROCESS_OPTIONS = [
  ...MM_PROCESS_OPTIONS,
  { value: "account_monitor", label: "account_monitor" },
];

export function MMControlPanel() {
  const { isMobileScreen } = useScreenSize();
  const { startMmProcess, stopMmProcess, restartMmProcess, stopAllProcesses, restartAllProcesses } = useMMMonitoring();

  const [selectedEnv, setSelectedEnv] = useState<string>(ENV_OPTIONS[0].value);
  const [selectedMmProcess, setSelectedMmProcess] = useState<string>(MM_PROCESS_OPTIONS[0].value);
  const [selectedAsset, setSelectedAsset] = useState<string>("ETH");
  const [manageAllEnv, setManageAllEnv] = useState<string>(ENV_OPTIONS[0].value);
  const [manageAllProcess, setManageAllProcess] = useState<string>(MM_PROCESS_OPTIONS[0].value);

  // Singletons (e.g. account_monitor) have no asset; send a neutral value the control server ignores.
  const isSingleton = SINGLETON_PROCESSES.includes(selectedMmProcess);
  const effectiveAsset = isSingleton ? "all" : selectedAsset;

  const onStartMmProcess = useCallback(async () => {
    if (selectedMmProcess && effectiveAsset && selectedEnv) {
      await startMmProcess(selectedMmProcess, effectiveAsset, selectedEnv);
    }
  }, [selectedMmProcess, effectiveAsset, selectedEnv, startMmProcess]);

  const onStopMmProcess = useCallback(async () => {
    if (selectedMmProcess && effectiveAsset && selectedEnv) {
      await stopMmProcess(selectedMmProcess, effectiveAsset, selectedEnv);
    }
  }, [selectedMmProcess, effectiveAsset, selectedEnv, stopMmProcess]);

  const onRestartMmProcess = useCallback(async () => {
    if (selectedMmProcess && effectiveAsset && selectedEnv) {
      await restartMmProcess(selectedMmProcess, effectiveAsset, selectedEnv);
    }
  }, [selectedMmProcess, effectiveAsset, selectedEnv, restartMmProcess]);

  const onKillAll = useCallback(async () => {
    if (manageAllProcess && manageAllEnv) {
      await stopAllProcesses(manageAllProcess, manageAllEnv);
    }
  }, [manageAllProcess, manageAllEnv, stopAllProcesses]);

  const onRestartAll = useCallback(async () => {
    if (manageAllProcess && manageAllEnv) {
      await restartAllProcesses(manageAllProcess, manageAllEnv);
    }
  }, [manageAllProcess, manageAllEnv, restartAllProcesses]);

  const rowStyle: React.CSSProperties = {
    display: "flex",
    gap: SPACING.two,
    alignItems: isMobileScreen ? "stretch" : "center",
    flexDirection: isMobileScreen ? "column" : "row",
    width: "100%",
  };

  return (
    <div
      style={{
        height: "calc(100vh - 60px)",
        overflow: "auto",
        padding: isMobileScreen ? SPACING.two : SPACING.four,
        paddingBottom: SPACING.four,
      }}
    >
      <div style={{ marginBottom: SPACING.four }}>
        <h2>MM Control Panel</h2>
        <div style={rowStyle}>
          <select
            value={selectedEnv}
            onChange={(e) => setSelectedEnv(e.target.value)}
            style={{
              width: isMobileScreen ? "100%" : 150,
              padding: "8px 12px",
              fontSize: "14px",
              borderRadius: "4px",
            }}
          >
            {ENV_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={selectedMmProcess}
            onChange={(e) => setSelectedMmProcess(e.target.value)}
            style={{
              width: isMobileScreen ? "100%" : 200,
              padding: "8px 12px",
              fontSize: "14px",
              borderRadius: "4px",
            }}
          >
            {SINGLE_PROCESS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {!isSingleton && (
            <div style={{ width: isMobileScreen ? "100%" : 200 }}>
              <MarketDropdown
                onClick={setSelectedAsset}
                overridePlaceholder="Select Asset"
              />
            </div>
          )}
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            disabled={!selectedMmProcess || !selectedEnv || (!isSingleton && !selectedAsset)}
            onClick={onStartMmProcess}
            style={isMobileScreen ? { width: "100%" } : undefined}
          >
            Start
          </Button>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            disabled={!selectedMmProcess || !selectedEnv || (!isSingleton && !selectedAsset)}
            onClick={onStopMmProcess}
            style={isMobileScreen ? { width: "100%" } : undefined}
          >
            Stop
          </Button>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            disabled={!selectedMmProcess || !selectedEnv || (!isSingleton && !selectedAsset)}
            onClick={onRestartMmProcess}
            style={isMobileScreen ? { width: "100%" } : undefined}
          >
            Restart
          </Button>
        </div>
      </div>

      {/* Visual Separator */}
      <div
        style={{
          height: "2px",
          background: "linear-gradient(to right, #333, #666, #333)",
          marginBottom: SPACING.four,
          marginTop: SPACING.four,
        }}
      />

      {/* Manage All Section */}
      <div style={{ marginBottom: SPACING.four }}>
        <h2>Manage All</h2>
        <div style={rowStyle}>
          <select
            value={manageAllEnv}
            onChange={(e) => setManageAllEnv(e.target.value)}
            style={{
              width: isMobileScreen ? "100%" : 150,
              padding: "8px 12px",
              fontSize: "14px",
              borderRadius: "4px",
            }}
          >
            {ENV_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={manageAllProcess}
            onChange={(e) => setManageAllProcess(e.target.value)}
            style={{
              width: isMobileScreen ? "100%" : 200,
              padding: "8px 12px",
              fontSize: "14px",
              borderRadius: "4px",
            }}
          >
            {MM_PROCESS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <Button
            buttonTheme={ButtonThemeEnum.SYSTEM}
            disabled={!manageAllProcess || !manageAllEnv}
            onClick={onKillAll}
            style={isMobileScreen ? { width: "100%" } : undefined}
          >
            Kill All
          </Button>
          <Button
            buttonTheme={ButtonThemeEnum.NEUTRAL2}
            disabled={!manageAllProcess || !manageAllEnv}
            onClick={onRestartAll}
            style={isMobileScreen ? { width: "100%" } : undefined}
          >
            Restart All
          </Button>
        </div>
      </div>
    </div>
  );
}
